import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, ArrowLeft, Loader, CheckCircle2, Clock, Package, Truck, Gift, Clipboard, Check
} from 'lucide-react';
import { useCart } from '../context/CartContext';
import { API_URL } from '../config';
import TopBar from '../components/TopBar';
import Header from '../components/Header';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Track.css';



const steps = [
  { status: 'PENDING', label: 'Order Placed', desc: 'Seller has received your order request.', icon: Clipboard },
  { status: 'PAYMENT_VERIFIED', label: 'Payment Confirmed', desc: 'Payment receipt verified by seller.', icon: Check },
  { status: 'PREPARING', label: 'Preparing', desc: 'Your premium nuts are being selected and processed.', icon: Clock },
  { status: 'PACKED', label: 'Packed', desc: 'Item packed and ready to ship.', icon: Package },
  { status: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', desc: 'Item is on the way to your delivery address.', icon: Truck },
  { status: 'DELIVERED', label: 'Delivered', desc: 'Order delivered successfully!', icon: Gift }
];

const Track = () => {
  const { token, wishlistCount, cartCount } = useCart();
  
  // State variables
  const [searchId, setSearchId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [trackedOrder, setTrackedOrder] = useState(null);
  
  const [userOrders, setUserOrders] = useState([]);
  const [loadingUserOrders, setLoadingUserOrders] = useState(false);

  // Load user orders if logged in
  useEffect(() => {
    if (token) {
      setLoadingUserOrders(true);
      fetch(`${API_URL}/user/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Failed to load user orders');
      })
      .then(data => {
        setUserOrders(data);
      })
      .catch(err => {
        console.error(err);
      })
      .finally(() => {
        setLoadingUserOrders(false);
      });
    }
  }, [token]);

  // Handle manual tracking lookup
  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    if (!searchId.trim()) return;

    setLoading(true);
    setError('');
    setTrackedOrder(null);

    try {
      const res = await fetch(`${API_URL}/orders/${searchId.trim()}`);
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || 'No order found with this tracking ID.');
      }

      setTrackedOrder(data);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error occurred while retrieving your order.');
    } finally {
      setLoading(false);
    }
  };

  // Find step completion details
  const getStatusIndex = (status) => {
    return steps.findIndex(s => s.status === status);
  };

  const currentStatusIndex = trackedOrder ? getStatusIndex(trackedOrder.status) : -1;

  return (
    <div className="track-page-wrapper">
      <TopBar />
      <Header wishlistCount={wishlistCount} cartCount={cartCount} />
      <Navbar activeItem="Track Order" />

      <main className="container track-main-content">
        {/* Back navigation */}
        <div style={{ marginBottom: '20px' }}>
          <Link to="/" className="back-home-link" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: 600, color: 'var(--primary-green)' }}>
            <ArrowLeft size={16} />
            <span>Back to Home</span>
          </Link>
        </div>

        <div className="track-grid-layout">
          {/* Left Panel: Search Order / Recent Orders */}
          <div className="track-search-section">
            <div className="track-card">
              <h2 className="track-card-title">Track Your Order</h2>
              <p className="track-card-subtitle">Enter your Order ID (received after checking out) below to view delivery progress.</p>
              
              <form onSubmit={handleTrackSubmit} className="track-search-form">
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value)}
                    placeholder="Enter 36-character Order ID"
                    className="form-input"
                    style={{ flex: 1 }}
                    required
                  />
                  <button type="submit" className="btn-search-submit" disabled={loading}>
                    {loading ? <Loader size={16} className="spin-animation" /> : <Search size={16} />}
                    <span>Track</span>
                  </button>
                </div>
              </form>

              {error && (
                <div className="track-error-box" style={{ marginTop: '12px', padding: '10px', backgroundColor: '#fdf2f2', borderLeft: '3px solid #f87171', borderRadius: '4px', color: '#b91c1c', fontSize: '13px' }}>
                  {error}
                </div>
              )}
            </div>

            {/* Logged in User's orders */}
            {token && (
              <div className="track-card" style={{ marginTop: '20px' }}>
                <h3 className="track-card-title" style={{ fontSize: '16px' }}>Your Recent Orders</h3>
                
                {loadingUserOrders ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Loader className="spin-animation" style={{ color: 'var(--primary-green)' }} />
                  </div>
                ) : userOrders.length === 0 ? (
                  <p style={{ fontSize: '13px', color: 'var(--gray)', margin: '10px 0 0 0' }}>You have not placed any orders yet.</p>
                ) : (
                  <div className="user-orders-list" style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {userOrders.map(order => (
                      <button
                        key={order.id}
                        onClick={() => {
                          setTrackedOrder(order);
                          setError('');
                        }}
                        style={{
                          textAlign: 'left',
                          width: '100%',
                          background: trackedOrder?.id === order.id ? 'rgba(46, 93, 52, 0.05)' : '#ffffff',
                          border: trackedOrder?.id === order.id ? '1px solid var(--primary-green)' : '1px solid var(--border-color)',
                          borderRadius: '8px',
                          padding: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease'
                        }}
                        className="user-order-item"
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 700, color: 'var(--dark-green)' }}>Order ID: {order.id.slice(0, 8)}...</span>
                          <span style={{ fontSize: '11px', color: 'var(--gray)' }}>{new Date(order.createdAt).toLocaleDateString()}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--dark-text)' }}>₹{order.totalAmount}</span>
                          <span style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            color: order.status === 'DELIVERED' ? 'var(--primary-green)' : '#d97706',
                            textTransform: 'uppercase'
                          }}>
                            {order.status.replace(/_/g, ' ')}
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Right Panel: Detailed Timeline Tracker (Flipkart Style) */}
          <div className="track-details-section">
            {trackedOrder ? (
              <div className="track-card">
                <div className="tracker-header">
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: 700, color: 'var(--dark-green)', margin: 0 }}>
                      Delivery Progress
                    </h3>
                    <span style={{ fontSize: '12px', color: 'var(--gray)' }}>
                      Order ID: <strong style={{ color: 'var(--dark-text)' }}>{trackedOrder.id}</strong>
                    </span>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: '12px', color: 'var(--gray)', display: 'block' }}>Total Amount</span>
                    <strong style={{ fontSize: '18px', color: 'var(--dark-green)' }}>₹{trackedOrder.totalAmount}</strong>
                  </div>
                </div>

                {/* Shipping Details */}
                <div className="tracker-shipping-box">
                  <h4 style={{ fontSize: '12px', textTransform: 'uppercase', color: 'var(--gray)', margin: '0 0 8px 0', letterSpacing: '0.5px' }}>
                    Shipping Details
                  </h4>
                  <p style={{ fontSize: '13px', margin: '0 0 4px 0', color: 'var(--dark-text)' }}>
                    <strong>Name:</strong> {trackedOrder.customerName}
                  </p>
                  <p style={{ fontSize: '13px', margin: '0 0 4px 0', color: 'var(--dark-text)' }}>
                    <strong>Phone:</strong> {trackedOrder.phone}
                  </p>
                  <p style={{ fontSize: '13px', margin: 0, color: 'var(--gray)', lineHeight: '1.4' }}>
                    <strong>Address:</strong> {trackedOrder.address}
                  </p>
                </div>

                {/* Flipkart Style Timeline Stepper */}
                <div className="tracker-timeline-stepper">
                  {steps.map((step, index) => {
                    const StepIcon = step.icon;
                    const isCompleted = index <= currentStatusIndex;
                    const isActive = index === currentStatusIndex;
                    const hasNext = index < steps.length - 1;
                    const isLineCompleted = index < currentStatusIndex;

                    return (
                      <div key={step.status} className={`stepper-node ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''}`}>
                        {/* Timeline Graphic column */}
                        <div className="stepper-graphic">
                          <div className={`stepper-circle ${isCompleted ? 'active-circle' : ''}`}>
                            {isCompleted ? <CheckCircle2 size={16} /> : <StepIcon size={14} />}
                          </div>
                          {hasNext && (
                            <div className={`stepper-line ${isLineCompleted ? 'active-line' : ''}`} />
                          )}
                        </div>

                        {/* Timeline content column */}
                        <div className="stepper-content">
                          <h4 style={{ fontSize: '14px', fontWeight: 700, margin: '0 0 4px 0', color: isCompleted ? 'var(--dark-green)' : 'var(--gray)' }}>
                            {step.label}
                          </h4>
                          <p style={{ fontSize: '12px', margin: 0, color: 'var(--gray)', lineHeight: '1.4' }}>
                            {step.desc}
                          </p>
                          {isActive && (
                            <span style={{ fontSize: '10px', color: 'var(--primary-green)', fontWeight: 600, display: 'block', marginTop: '6px' }}>
                              Status Last Updated: {new Date(trackedOrder.updatedAt).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="track-card track-placeholder-box" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px 20px', textAlign: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '64px', height: '64px', borderRadius: '50%', backgroundColor: 'rgba(46, 93, 52, 0.05)', color: 'var(--primary-green)', marginBottom: '16px' }}>
                  <Search size={28} />
                </div>
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--dark-green)' }}>No Order Selected</h3>
                <p style={{ fontSize: '13px', color: 'var(--gray)', maxWidth: '280px', marginTop: '6px', lineHeight: '1.4' }}>
                  Enter your order tracking ID or select one of your recent orders from the left panel to track live delivery status.
                </p>
              </div>
            )}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Track;
